import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  ChevronRight,
  Clock,
  //   Facebook,
  //   Linkedin,
  Link as LinkIcon,
  MapPin,
  Quote,
  Sparkles,
  Tag,
  //   Twitter,
} from "lucide-react";
import { FaLinkedin, FaTwitter, FaFacebook } from "react-icons/fa";
import { blogs, getBlogById } from "../../../data/blogs";
import "./BlogsPost.css";

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = getBlogById(slug) ?? blogs[0];
  const [copied, setCopied] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 24,
    mass: 0.4,
  });

  useEffect(() => {
  window.scrollTo({ top: 0, behavior: "smooth" });
}, [slug]); // better than post.id

 const related = useMemo(() => {
  const currentKey = post.id || post.slug;

  return blogs
    .filter((b) => (b.id || b.slug) !== currentKey)
    .slice(0, 3);
}, [post.id, post.slug]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = encodeURIComponent(post.title);
  const initials = post.author
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("");

  // Build TOC from h2 blocks
  const toc = useMemo(
    () =>
      post.content
        .map((b, i) => (b.type === "h2" ? { id: `s-${i}`, text: b.text } : null))
        .filter(Boolean),
    [post.content]
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* noop */
    }
  };

  // Split first paragraph for drop-cap
  const firstParaIdx = post.content.findIndex((b) => b.type === "p");

  return (
    <div className="bpx">
      {/* reading progress */}
      <motion.div className="bpx-progress" style={{ scaleX: progress }} />

      {/* HERO — cinematic split */}
      <section className="bpx-hero">
        <div className="bpx-hero__bg" aria-hidden />
        <div className="bpx-hero__grid">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
            className="bpx-hero__left"
          >
            <motion.div variants={fadeUp} className="bpx-crumbs">
              <Link to="/">Home</Link>
              <ChevronRight size={13} />
              <Link to="/blogs">Blogs</Link>
              <ChevronRight size={13} />
              <span>{post.category}</span>
            </motion.div> <br />
            <motion.span variants={fadeUp} className="bpx-kicker">
              <Sparkles size={13} /> {post.category}
            </motion.span>
            <motion.h1 variants={fadeUp} className="bpx-title">
              {post.title}
            </motion.h1>
            <motion.p variants={fadeUp} className="bpx-lede">
              {post.subtitle}
            </motion.p>
            <motion.div variants={fadeUp} className="bpx-metaRow">
              <div className="bpx-author">
                <div className="bpx-avatar">{initials}</div>
                <div>
                  <strong>{post.author}</strong>
                  <span>{post.role}</span>
                </div>
              </div>
              <div className="bpx-metaChips">
                <span><Calendar size={13} /> {post.date}</span>
                {post.location && <span><MapPin size={13} /> {post.location}</span>}
                <span><Clock size={13} /> {post.read}</span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="bpx-hero__right"
          >
            <div className="bpx-hero__frame">
              <img src={post.image} alt={post.title} />
              <div className="bpx-hero__tag">
                <Tag size={12} /> {post.tag ?? post.category}
              </div>
              <div className="bpx-hero__note">
                <span>{post.read}</span>
                <span className="dot" />
                <span>{post.date}</span>
              </div>
            </div>
            <div className="bpx-hero__decoA" aria-hidden />
            <div className="bpx-hero__decoB" aria-hidden />
          </motion.div>
        </div>
      </section>

      {/* BODY layout */}
      <section className="bpx-body container">
        {/* LEFT rail — TOC + share */}
        <aside className="bpx-rail">
          {toc.length > 0 && (
            <div className="bpx-toc">
              <div className="bpx-toc__label">In this article</div>
              <ul>
                {toc.map((t, i) => (
                  <li key={t.id}>
                    <a href={`#${t.id}`}>
                      <span className="bpx-toc__num">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {t.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="bpx-shareBox">
            <div className="bpx-toc__label">Share</div>
            <div className="bpx-shareBtns">
              <a
                aria-label="Share on Twitter"
                href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noreferrer"
              >
                <FaTwitter size={15} />
              </a>
              <a
                aria-label="Share on LinkedIn"
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noreferrer"
              >
                <FaLinkedin size={15} />
              </a>
              <a
                aria-label="Share on Facebook"
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noreferrer"
              >
                <FaFacebook size={15} />
              </a>
              <button aria-label="Copy link" onClick={handleCopy}>
                <LinkIcon size={15} />
              </button>
            </div>
            {copied && <div className="bpx-copied">Link copied!</div>}
          </div>
        </aside>

        {/* ARTICLE */}
        <motion.article
          key={`article-${post.id}`}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bpx-article"
        >
          {(() => {
            let h2Count = 0;
            return post.content.map((block, i) => {
              if (block.type === "h2") {
                h2Count += 1;
                return (
                  <h2 key={i} id={`s-${i}`} className="bpx-h2">
                    <span className="bpx-h2__num">
                      {String(h2Count).padStart(2, "0")}
                    </span>
                    <span>{block.text}</span>
                  </h2>
                );
              }
              if (block.type === "quote") {
                return (
                  <blockquote key={i} className="bpx-quote">
                    <Quote className="bpx-quote__icon" size={40} />
                    <p>{block.text}</p>
                    <cite>— {block.author}</cite>
                  </blockquote>
                );
              }
              if (block.type === "list") {
                return (
                  <ul key={i} className="bpx-list">
                    {block.items.map((it, j) => (
                      <li key={j}>
                        <span className="bpx-list__mark">
                          {String(j + 1).padStart(2, "0")}
                        </span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
               if (block.type === "flow") {
                const layout = block.layout ?? "vertical";
                return (
                  <figure key={i} className={`bpx-flow bpx-flow--${layout}`}>
                    {block.title && <figcaption className="bpx-flow__title">{block.title}</figcaption>}
                    <ol className="bpx-flow__track">
                      {block.nodes.map((n, j) => (
                        <li
                          key={j}
                          className={`bpx-flow__node bpx-flow__node--${n.kind ?? "process"}`}
                        >
                          <span className="bpx-flow__step">{String(j + 1).padStart(2, "0")}</span>
                          <div className="bpx-flow__body">
                            <h4>{n.label}</h4>
                            {n.sub && <p className="bpx-flow__sub">{n.sub}</p>}
                            {n.items && (
                              <ul className="bpx-flow__items">
                                {n.items.map((it, k) => (
                                  <li key={k}>{it}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </li>
                      ))}
                    </ol>
                    {block.caption && <p className="bpx-flow__caption">{block.caption}</p>}
                  </figure>
                );
              }
         
              const dropCap = i === firstParaIdx;
              return (
                <p key={i} className={dropCap ? "bpx-p bpx-p--drop" : "bpx-p"}>
                  {block.text}
                </p>
              );
            });
          })()}
          <div className="bpx-tags">
            {post.tags.map((t) => (
              <span key={t} className="bpx-tag">#{t}</span>
            ))}
          </div>

          <div className="bpx-authorCard">
            <div className="bpx-authorCard__avatar">{initials}</div>
            <div className="bpx-authorCard__body">
              <div className="bpx-authorCard__head">
                <strong>{post.author}</strong>
                <span>{post.role}</span>
              </div>
              <p>
                Stories, updates and ideas from the team behind SpringUp Labs —
                building digital products that move businesses forward.
              </p>
              <Link to="/blogs" className="bpx-authorCard__cta">
                More from SpringUp <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </motion.article>
      </section>

      {/* RELATED */}
      <section className="bpx-related">
        <div className="bpx-related__head">
          <div>
            <span className="bpx-related__eyebrow">More stories</span>
            <h3>Keep reading</h3>
          </div>
          <Link to="/blogs" className="bpx-related__all">
            All articles <ArrowRight size={15} />
          </Link>
        </div>
        <motion.div
        key={`related-${post.id || post.slug}`}  // remount on post change
          initial="hidden"
          animate="show"
          // viewport={{ once: true, amount: 0.1 }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
          className="bpx-rgrid"
        >
          {related.map((r) => (
            <motion.article key={r.id} variants={fadeUp} className="bpx-rcard">
              <Link to={`/blogs/${r.slug}`} className="bpx-rcard__media">
                <img src={r.image} alt={r.title} />
                <span className="bpx-rcard__cat">{r.category}</span>
              </Link>
              <div className="bpx-rcard__body">
                <h4>
                  <Link to={`/blogs/${r.slug}`}>{r.title}</Link>
                </h4>
                <p>{r.excerpt}</p>
                <div className="bpx-rcard__meta">
                  <span><Clock size={13} /> {r.read}</span>
                  <Link to={`/blogs/${r.slug}`} className="bpx-rcard__link">
                    Read <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </section>

      <section className="bpx-back">
        <Link to="/blogs" className="bpx-backBtn">
          <ArrowLeft size={16} /> Back to all blogs
        </Link>
      </section>
    </div>
  );
};

export default BlogPost;